import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import './Admin.css';

// Admin Components
import SlideManager from '../components/admin/SlideManager';
import CategoryManager from '../components/admin/CategoryManager';
import ServiceManager from '../components/admin/ServiceManager';
import ProjectManager from '../components/admin/ProjectManager';
import ChatConfigManager from '../components/admin/ChatConfigManager';
import MenuManager from '../components/admin/MenuManager';
import ApplicationsManager from '../components/admin/ApplicationsManager';
import InquiriesManager from '../components/admin/InquiriesManager';
import StatsManager from '../components/admin/StatsManager';
import AboutEditor from '../components/admin/AboutEditor';

function Admin() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = JSON.parse(localStorage.getItem('user') || '{}');

    if (!token || userData.role !== 'admin') {
      navigate('/login');
      return;
    }

    setUser(userData);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (!user) {
    return <div className="loading-container">Loading...</div>;
  }

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-logo">
          <img src="/staticimages/be50grlogo.png" alt="be50.gr" className="logo-image" />
          <span className="logo-text">be50.gr Admin</span>
        </div>

        <nav className="admin-nav">
          <Link to="/admin" className="admin-nav-link">
            <span className="nav-icon">📊</span>
            Dashboard
          </Link>
          <Link to="/admin/projects" className="admin-nav-link">
            <span className="nav-icon">📋</span>
            Έργα
          </Link>
          <Link to="/admin/menu" className="admin-nav-link">
            <span className="nav-icon">🗂️</span>
            Μενού
          </Link>
          <Link to="/admin/applications" className="admin-nav-link">
            <span className="nav-icon">👥</span>
            Αιτήσεις Freelancers
          </Link>
          <Link to="/admin/inquiries" className="admin-nav-link">
            <span className="nav-icon">💬</span>
            Μηνύματα
          </Link>
          <Link to="/admin/stats" className="admin-nav-link">
            <span className="nav-icon">📊</span>
            Στατιστικά
          </Link>
          <Link to="/admin/slides" className="admin-nav-link">
            <span className="nav-icon">🎬</span>
            Hero Slides
          </Link>
          <Link to="/admin/categories" className="admin-nav-link">
            <span className="nav-icon">📂</span>
            Κατηγορίες
          </Link>
          <Link to="/admin/services" className="admin-nav-link">
            <span className="nav-icon">💼</span>
            Υπηρεσίες
          </Link>
          <Link to="/admin/about" className="admin-nav-link">
            <span className="nav-icon">ℹ️</span>
            Σχετικά με εμάς
          </Link>
          <Link to="/admin/ai-config" className="admin-nav-link">
            <span className="nav-icon">🤖</span>
            AI Chatbot
          </Link>
        </nav>

        <div className="admin-user">
          <div className="user-avatar">👤</div>
          <div className="user-info">
            <div className="user-name">{user.name}</div>
            <div className="user-role">Administrator</div>
          </div>
          <button onClick={handleLogout} className="logout-btn" title="Αποσύνδεση">
            🚪
          </button>
        </div>
      </aside>

      <main className="admin-content">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/projects" element={<ProjectManager />} />
          <Route path="/menu" element={<MenuManager />} />
          <Route path="/applications" element={<ApplicationsManager />} />
          <Route path="/inquiries" element={<InquiriesManager />} />
          <Route path="/stats" element={<StatsManager />} />
          <Route path="/slides" element={<SlideManager />} />
          <Route path="/categories" element={<CategoryManager />} />
          <Route path="/services" element={<ServiceManager />} />
          <Route path="/about" element={<AboutEditor />} />
          <Route path="/ai-config" element={<ChatConfigManager />} />
        </Routes>
      </main>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState({
    slides: 0,
    categories: 0,
    services: 0,
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [slidesRes, categoriesRes, servicesRes] = await Promise.all([
        fetch('/api/slides/all', { headers }),
        fetch('/api/categories/all', { headers }),
        fetch('/api/services/all', { headers }),
      ]);

      const slides = await slidesRes.json();
      const categories = await categoriesRes.json();
      const services = await servicesRes.json();

      setStats({
        slides: slides.length,
        categories: categories.length,
        services: services.length,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  return (
    <div className="dashboard">
      <h1 className="page-title">Dashboard</h1>
      <p className="page-subtitle">Καλώς ήρθατε στο Admin Panel</p>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">🎬</div>
          <div className="stat-info">
            <div className="stat-value">{stats.slides}</div>
            <div className="stat-label">Hero Slides</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📂</div>
          <div className="stat-info">
            <div className="stat-value">{stats.categories}</div>
            <div className="stat-label">Κατηγορίες</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💼</div>
          <div className="stat-info">
            <div className="stat-value">{stats.services}</div>
            <div className="stat-label">Υπηρεσίες</div>
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <h2>Γρήγορες Ενέργειες</h2>
        <div className="actions-grid">
          <Link to="/admin/slides" className="action-card">
            <span className="action-icon">➕</span>
            <span>Προσθήκη Slide</span>
          </Link>
          <Link to="/admin/categories" className="action-card">
            <span className="action-icon">➕</span>
            <span>Προσθήκη Κατηγορίας</span>
          </Link>
          <Link to="/admin/services" className="action-card">
            <span className="action-icon">➕</span>
            <span>Προσθήκη Υπηρεσίας</span>
          </Link>
          <Link to="/" className="action-card">
            <span className="action-icon">👁️</span>
            <span>Προβολή Site</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Admin;
